/* eslint-disable react/prop-types */
import React, { useMemo, useCallback } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor } from 'helpers/utils';
import PropTypes from 'prop-types';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  DatasetComponent
]);

const colors = ['#5470C6', '#ffd000', '#ff4f4f', '#91cc75', '#c966ed'];

const isLineChart = years => {
  return ['Average', 'Mean'].includes(years);
};
const getChartOption = (data, series, years) => {
  const seriesData = years.map((year, yearIndex) => ({
    name: year,
    type: isLineChart(year) ? 'line' : 'bar',
    data: series.map(seriesName => data[year][series.indexOf(seriesName)] || 0),
    lineStyle: {
      color: isLineChart(year) && '#c966ed'
    },
    itemStyle: {
      borderRadius: [3, 3, 0, 0],
      color: isLineChart(year) && '#c966ed'
    },
    symbol: 'circle',
    symbolSize: 10,
    barWidth: '20px'
  }));

  return {
    color: colors,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      transitionDuration: 0
      // valueFormatter: function (value) {
      //   return value + ` ${unit}`;
      // }
    },
    legend: {
      data: years,
      left: 0,
      top: 0,
      icon: 'circle',
      inactiveColor: getColor('gray-400'),
      textStyle: { color: getColor('gray-700') }
    },
    xAxis: {
      type: 'category',
      data: series,
      axisLine: {
        show: true,
        lineStyle: { color: getColor('gray-300'), type: 'solid' }
      },
      axisLabel: {
        color: getColor('gray-500'),
        rotate: 0,
        interval: 0
      },
      axisTick: { show: true },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: value => value,
        color: getColor('gray-500')
      },
      // name: '',
      axisLine: {
        show: true,
        lineStyle: { color: getColor('gray-300'), type: 'solid' }
      },
      splitLine: {
        lineStyle: { type: 'dashed', color: getColor('gray-200') }
      }
    },
    series: seriesData,
    grid: { right: 15, left: 60, bottom: '15%', top: '15%' }
  };
};

const BarChartBody = ({ landCoverChartData, onLegendSelect }) => {
  const { data, series, years } = useMemo(() => {
    const years = Object.keys(landCoverChartData.data);
    return {
      data: landCoverChartData.data,
      series: landCoverChartData.series,
      years: years
    };
  }, [landCoverChartData]);

  const chartOptions = useMemo(
    () => getChartOption(data, series, years),
    [data, series, years]
  );

  const onChartReady = useCallback(
    chart => {
      const handleLegendSelectChanged = params => {
        const selectedLegend = Object.keys(params.selected).filter(
          key => params.selected[key]
        );
        if (selectedLegend.length > 0) {
          onLegendSelect(selectedLegend[selectedLegend.length - 1]);
        }
      };

      chart.on('legendselectchanged', handleLegendSelectChanged);

      return () => {
        chart.off('legendselectchanged', handleLegendSelectChanged);
      };
    },
    [onLegendSelect]
  );

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={chartOptions}
      onChartReady={onChartReady}
      style={{ minHeight: '25.75rem' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};

BarChartBody.propTypes = {
  landCoverChartData: PropTypes.shape({
    data: PropTypes.object,
    series: PropTypes.arrayOf(PropTypes.string)
  }),
  onLegendSelect: PropTypes.func
};

export default BarChartBody;
